import { ModalBase, ModalStyle } from 'views/components/atoms/modal';
import './style.scss';
import {
  BrokerBranchInfoSearch,
  BrokerInfoSearch,
  BrokerInfoSelect,
} from 'views/components/organisms/brokerInfoSearch';
import { ReactNode, useState } from 'react';
import { Button, ButtonSize, FillType } from 'views/components/atoms/button';
import {
  CodeNameMap,
  useBranchInfoSearch,
  useBrokerInfoSearch,
} from 'views/components/organisms/brokerInfoSearch/hooks';
import { findIcon, IconName } from 'views/utils/handleIcon';
import { IconSize, SvgIcon } from 'views/components/atoms/svgIcon';

type InputBankModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setBankInfo: (bankName: string, bankCode: string, branchName: string, branchCode: string) => void;
};

enum InputType {
  // 金融機関名検索
  BankInfoSearch,
  // 金融機関名選択
  BankInfoSelect,
  // 支店名検索
  BranchInfoSearch,
  // 支店名セレクト
  BranchInfoSelect,
}

export const InputBankModal = (props: InputBankModalProps) => {
  const { isOpen, setIsOpen, setBankInfo } = props;
  // 選択した金融機関名と支店名
  const [selectedBrokerName, setSelectedBrokerName] = useState({ code: '', name: '' });
  const [selectedBranchName, setSelectedBranchName] = useState({ code: '', name: '' });

  // 検索用の金融機関名と金融機関コード
  const [brokerName, setBrokerName] = useState<string>('');
  const [brokerCode, setBrokerCode] = useState<string>('');

  // 検索用の支店名と視点コード
  const [branchName, setBranchName] = useState<string>('');
  const [branchCode, setBranchCode] = useState<string>('');

  // 金融機関名と支店名の検索結果
  const [brokerList, setBrokerList] = useState<CodeNameMap[]>([]);
  const [branchList, setBranchList] = useState<CodeNameMap[]>([]);

  // 画面切替ステート
  const [inputState, setInputState] = useState(InputType.BankInfoSearch);

  // 金融機関名と支店名の検索メソッド
  const brokerInfoSearchProp = useBrokerInfoSearch((data) => {
    setBrokerList(data);
    setInputState(InputType.BankInfoSelect);
  });
  const branchInfoSearchProp = useBranchInfoSearch(selectedBrokerName.code, (data) => {
    setBranchList(data);
    setInputState(InputType.BranchInfoSelect);
  });

  const factory = [
    {
      inputType: InputType.BankInfoSearch,
      title: '金融機関名の検索',
      input: (
        <BrokerInfoSearch
          name={brokerName}
          setName={setBrokerName}
          code={brokerCode}
          setCode={setBrokerCode}
          {...brokerInfoSearchProp}
        />
      ),
    },
    {
      inputType: InputType.BankInfoSelect,
      title: '金融機関の選択',
      subTitle: '金融機関名を選択し、「選択」ボタンを押してください。',
      input: (
        <BrokerInfoSelect
          brokerInfoList={brokerList}
          setBrokerInfo={setSelectedBrokerName}
          back={() => {
            // 結果クリアして戻る
            setBrokerList([]);
            setInputState(InputType.BankInfoSearch);
          }}
          next={() => {
            setInputState(InputType.BranchInfoSearch);
          }}
        />
      ),
    },
    {
      inputType: InputType.BranchInfoSearch,
      title: '支店名の検索',
      subTitle: '金融機関名をご確認いただき、支店名を選択してください。',
      selectedBrokerName: selectedBrokerName.name,
      input: (
        <BrokerBranchInfoSearch
          name={branchName}
          setName={setBranchName}
          code={branchCode}
          setCode={setBranchCode}
          {...branchInfoSearchProp}
          onClickPrev={() => {
            // 結果クリアして戻る
            setBranchList([]);
            setInputState(InputType.BankInfoSelect);
          }}
        />
      ),
    },
    {
      inputType: InputType.BranchInfoSelect,
      title: '支店名の選択',
      subTitle: '支店名を選択し、「選択」ボタンを押してください',
      selectedBrokerName: selectedBrokerName.name,
      input: (
        <BrokerInfoSelect
          brokerInfoList={branchList}
          setBrokerInfo={setSelectedBranchName}
          back={() => {
            // 結果クリアして戻る
            setBranchList([]);
            setInputState(InputType.BranchInfoSearch);
          }}
          next={() => {
            // 選んでもらった銀行情報をセット
            setBankInfo(
              selectedBrokerName.name,
              selectedBrokerName.code,
              selectedBranchName.name,
              selectedBranchName.code,
            );
            // 最後まで行ったらモーダルクローズ
            setInputState(InputType.BankInfoSearch);
            setIsOpen(false);
          }}
        />
      ),
    },
  ];

  return (
    <ModalBase
      isOpen={isOpen}
      modalStyle={ModalStyle.confirmModal}
      children={
        <div className="inputBankModal">
          <BankModalTemplate
            {...factory.find((x) => x.inputType === inputState)}
            onCancel={() => {
              setInputState(InputType.BankInfoSearch);
              setIsOpen(false);
            }}
          />
        </div>
      }
    />
  );
};

type Props = {
  title?: string;
  subTitle?: string;
  selectedBrokerName?: string;
  input?: ReactNode;
  onCancel: () => void;
};
const BankModalTemplate = (props: Props) => {
  const { title, subTitle, selectedBrokerName, input, onCancel } = props;
  const icon = findIcon(IconName.AuroraBox);
  return (
    <>
      <div className="inputBankModal_title">
        <SvgIcon icon={icon} size={IconSize.icon_m} />
        <div className="inputBankModal_title_label">{title}</div>
      </div>
      {selectedBrokerName && (
        <div className="inputBankModal_selectedBrokerName">
          <div className="inputBankModal_selectedBrokerName_title">金融機関名</div>
          <div className="inputBankModal_selectedBrokerName_name">{selectedBrokerName}</div>
        </div>
      )}
      {subTitle && <div className="inputBankModal_subtitle">{subTitle}</div>}
      <div className="inputBankModal_inputSearch">{input}</div>
      <Button label="キャンセル" buttonSize={ButtonSize.sizeS} fillType={FillType.primaryEmpty} onClick={onCancel} />
    </>
  );
};
